import { sdk } from '@/lib/api';
import { FetchError } from '@/lib/api/client';
import { queryKeysFactory } from '@/lib/query-key-factory';
import { HttpTypes } from '@/types';
import {
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';

const PROMOTION_LIST_KEY = 'promotion-list' as const;
const PRODUCT_GIFT_REWARD_LIST_KEY = 'product-gift-reward-list' as const;
const PRODUCT_GIFT_REWARD_ITEM_LIST_KEY = 'product-gift-reward-item-list' as const;
const PRODUCT_GIFT_REWARD_LIST_INFINITE_KEY = 'product-gift-reward-list-infinite' as const;
const PRODUCT_GIFT_REWARD_ITEM_LIST_INFINITE_KEY =
  'product-gift-reward-item-list-infinite' as const;
const PROMOTION_PRODUCT_LIST_KEY = 'promotion-product-list' as const;

export const promotionListQueryKeys: ReturnType<typeof queryKeysFactory> =
  queryKeysFactory(PROMOTION_LIST_KEY);
export const productGiftRewardQueryKeys = {
  listGroup: (promoId: string, params?: HttpTypes.FindParams) =>
    [PRODUCT_GIFT_REWARD_LIST_KEY, 'list', { promoId, params }] as const,
  listGroupInfinite: (promoId: string, params?: HttpTypes.FindParams) =>
    [PRODUCT_GIFT_REWARD_LIST_INFINITE_KEY, 'list', { promoId, params }] as const,
  ListItem: (promoId: string, productId: string, params?: HttpTypes.FindParams) =>
    [PRODUCT_GIFT_REWARD_ITEM_LIST_KEY, 'list', { promoId, productId, params }] as const,
  listItemInfinite: (promoId: string, productId: string, params?: HttpTypes.FindParams) =>
    [PRODUCT_GIFT_REWARD_ITEM_LIST_INFINITE_KEY, 'list', { promoId, productId, params }] as const,
};
export const promotionProductListQueryKeys: ReturnType<typeof queryKeysFactory> = queryKeysFactory(
  PROMOTION_PRODUCT_LIST_KEY
);

export const useGetListPromotions = (
  payload: HttpTypes.StorePromotionListPayload,
  is_recommend: boolean,
  token: string,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.StorePromotionListResponse,
      FetchError,
      HttpTypes.StorePromotionListResponse,
      QueryKey
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: promotionListQueryKeys.list({ ...payload, is_recommend }),
    queryFn: async () => sdk.site.promotion.list(payload, is_recommend, token),
    enabled: options?.enabled,
    ...options,
  });

  return { ...data, ...rest };
};

export const useGetRewardProduct = (
  payload: HttpTypes.StorePromotionRewardProductPayload & {
    token: string;
  },
  query?: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.StorePromotionProductRewardList,
      FetchError,
      HttpTypes.StorePromotionProductRewardList,
      QueryKey
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: productGiftRewardQueryKeys.listGroup(payload.promotion_id, query),
    queryFn: async () =>
      sdk.site.promotion.reward(
        {
          promotion_id: payload.promotion_id,
          ...(payload.promotion_tier_id != null
            ? { promotion_tier_id: payload.promotion_tier_id }
            : {}),
        },
        query,
        payload.token
      ),
    enabled: options?.enabled,
    ...options,
  });

  return { ...data, ...rest };
};

export const useGetRewardProductInfinite = (
  payload: HttpTypes.StorePromotionRewardProductPayload & {
    token: string;
    locationId: string;
  },
  query?: HttpTypes.FindParams,
  options?: Omit<
    UseInfiniteQueryOptions<
      HttpTypes.StorePromotionProductRewardListResponseInfinite,
      FetchError,
      HttpTypes.StorePromotionProductRewardListResponseInfinite,
      HttpTypes.StorePromotionProductRewardListResponseInfinite,
      QueryKey
    >,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >
) => {
  return useInfiniteQuery({
    queryKey: productGiftRewardQueryKeys.listGroupInfinite(payload.promotion_id, query),
    queryFn: async ({ pageParam = 1 }) => {
      const result = await sdk.site.promotion.reward(
        {
          promotion_id: payload.promotion_id,
          ...(payload.promotion_tier_id != null
            ? { promotion_tier_id: payload.promotion_tier_id }
            : {}),
        },
        { ...query, page: pageParam as number },
        payload.token,
        payload.locationId
      );
      return {
        data: result?.data ?? [],
        totalCount: result.totalCount ?? 0,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages?.length;
      const pageSize = lastPage.data?.length;

      if (currentPage * pageSize < Number(lastPage.totalCount)) {
        return currentPage + 1;
      }
      return undefined;
    },
    enabled: options?.enabled,
    ...options,
  });
};

export const useGetRewardProductItem = (
  payload: HttpTypes.StorePromotionRewardProductItemPayload & {
    token: string;
  },
  query?: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.StorePromotionProductRewardListItem,
      FetchError,
      HttpTypes.StorePromotionProductRewardListItem,
      QueryKey
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: productGiftRewardQueryKeys.ListItem(payload.promotion_id, payload.product_id, query),
    queryFn: async () =>
      sdk.site.promotion.rewardItem(
        {
          promotion_id: payload.promotion_id,
          product_id: payload.product_id,
          ...(payload.promotion_tier_id != null
            ? { promotion_tier_id: payload.promotion_tier_id }
            : {}),
        },
        query,
        payload.token
      ),
    enabled: options?.enabled,
    ...options,
  });

  return { ...data, ...rest };
};

export const useGetRewardProductItemInfinite = (
  payload: HttpTypes.StorePromotionRewardProductItemPayload & {
    token: string;
    locationId: string;
  },
  query?: HttpTypes.FindParams,
  options?: Omit<
    UseInfiniteQueryOptions<
      HttpTypes.StorePromotionProductRewardListItemInfinite,
      FetchError,
      HttpTypes.StorePromotionProductRewardListItemInfinite,
      HttpTypes.StorePromotionProductRewardListItemInfinite,
      QueryKey
    >,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >
) => {
  return useInfiniteQuery({
    queryKey: productGiftRewardQueryKeys.listItemInfinite(
      payload.promotion_id,
      payload.product_id,
      query
    ),
    queryFn: async ({ pageParam = 1 }) => {
      const result = await sdk.site.promotion.rewardItem(
        {
          promotion_id: payload.promotion_id,
          product_id: payload.product_id,
          ...(payload.promotion_tier_id != null
            ? { promotion_tier_id: payload.promotion_tier_id }
            : {}),
        },
        { ...query, page: pageParam as number },
        payload.token,
        payload.locationId
      );
      return {
        data: result?.data ?? [],
        totalCount: result.totalCount ?? 0,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length;
      const pageSize = lastPage.data?.length;

      if (currentPage * pageSize < Number(lastPage.totalCount)) {
        return currentPage + 1;
      }
      return undefined;
    },
    enabled: options?.enabled,
    ...options,
  });
};

export const useMutateValidatePromotion = (
  options?: UseMutationOptions<
    HttpTypes.StorePromotionValidatePromoResponse,
    FetchError,
    {
      body: HttpTypes.StorePromotionPromoPayload;
      token: string;
    }
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.site.promotion.validatePromo(payload.body, payload.token),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useMutateValidateVoucher = (
  options?: UseMutationOptions<
    HttpTypes.StorePromotionValidateVoucherResponse,
    FetchError,
    {
      body: HttpTypes.StorePromotionVoucherPayload;
      token: string;
    }
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.site.promotion.validateVoucher(payload.body, payload.token),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useMutateApplyPromotion = (
  options?: UseMutationOptions<
    HttpTypes.StorePromotionApplyResponse,
    FetchError,
    {
      body: HttpTypes.StorePromotionApplyPayload;
      token?: string;
    }
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      payload.token
        ? sdk.site.promotion.apply(payload.body, payload.token)
        : sdk.site.promotion.apply(payload.body),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useMutateResetPromotion = (
  options?: UseMutationOptions<
    unknown,
    FetchError,
    {
      cartId: string;
      token?: string;
    }
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      payload.token
        ? sdk.site.promotion.reset(payload.cartId, payload.token)
        : sdk.site.promotion.reset(payload.cartId),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useMutateForcePromotion = (
  options?: UseMutationOptions<
    HttpTypes.StorePromotionForceResponse,
    FetchError,
    {
      checkoutId: string;
      email?: string;
      token?: string;
    }
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      payload.token
        ? sdk.site.promotion.force(payload.checkoutId, payload.token)
        : sdk.site.promotion.force(payload.checkoutId, payload.email),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useGetListProductPromotions = (
  payload: {
    slug: string;
    token: string;
  },
  options?: Omit<
    UseQueryOptions<
      HttpTypes.StorePromotionListResponse,
      FetchError,
      HttpTypes.StorePromotionListResponse,
      QueryKey
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: promotionProductListQueryKeys.list({ ...payload }),
    queryFn: async () => sdk.site.promotion.listProductPromo(payload.slug, payload.token),
    enabled: options?.enabled,
    ...options,
  });

  return { ...data, ...rest };
};
